// Promotional coupons of tallerza.cl, stored in `cupones/{CODIGO}` (id = the
// code). The superadmin manages them in the backoffice (rules: create with
// usos 0, never touch usos/codigo/creadoEn/creadoPor, no delete); only the
// `convertirContacto` callable increments `usos`, when the superadmin turns a
// lead into a customer (a public form cannot spend a coupon's uses).
// Contract: docs/comercial/lanzamiento.md ("Contrato técnico").
import * as z from 'zod/mini';
import {
  REGEX_CODIGO_CUPON,
  normalizarCodigoCupon,
  type ContactoDemo,
  type ContactoEspera,
} from './contacto.ts';

export const TIPOS_CUPON = ['porcentaje', 'meses_gratis'] as const;
export type TipoCupon = (typeof TIPOS_CUPON)[number];

/**
 * Who may use a coupon (business plan, docs/comercial/plan-de-negocio.md §5.3
 * and §10 #6). `campana`: a public code of tallerza.cl (social networks, a
 * distributor), always a percentage of 1–20 % for 1–3 months. `interno`:
 * never valid on the site (`/api/cupon` and the form reject it); Kalfuntech
 * applies it by hand in the workshop's commercial record (a goodwill gesture,
 * a negotiated deal). Free months exist only as internal coupons.
 */
export const USOS_CUPON = ['campana', 'interno'] as const;
export type UsoCupon = (typeof USOS_CUPON)[number];

export const ETIQUETAS_USO_CUPON: Record<UsoCupon, string> = {
  campana: 'Campaña (tallerza.cl)',
  interno: 'Interno',
};

/**
 * Business plan (§5.1): discounts are expressed in %, never as free months.
 * `meses_gratis` is for internal use only, never for a public campaign.
 */
export const USO_INTERNO_CUPON: Record<TipoCupon, boolean> = {
  porcentaje: false,
  meses_gratis: true,
};

export const ETIQUETAS_TIPO_CUPON: Record<TipoCupon, string> = {
  porcentaje: 'Porcentaje de descuento',
  meses_gratis: 'Meses gratis (uso interno)',
};

/**
 * Hard cap of any `porcentaje` coupon (schema and Firestore rules): the
 * plan's total discount cap is 35 % and a coupon never stacks with the launch
 * offer, so 30 % is the ceiling even for an internal one.
 */
export const MAX_PORCENTAJE_CUPON = 30;
/** Campaign coupons: at most 20 % for at most 3 months (§10 #6, a rule). */
export const MAX_PORCENTAJE_CAMPANA = 20;
export const MAX_DURACION_CAMPANA = 3;

/** Allowed `valor` per type (integers), whatever the use. */
export const RANGO_VALOR_CUPON: Record<TipoCupon, { min: number; max: number }> = {
  porcentaje: { min: 1, max: MAX_PORCENTAJE_CUPON },
  meses_gratis: { min: 1, max: 12 },
};

/**
 * Months the benefit lasts (integer). Always stored: for `porcentaje` it is
 * how many monthly bills get the discount; for `meses_gratis` it is the
 * number of free months, so it always equals `valor` (the schema fixes it,
 * the rules require it). One field with one meaning for every consumer.
 */
export const RANGO_DURACION_CUPON = { min: 1, max: 12 } as const;

type Rango = { min: number; max: number };

/**
 * `valor` and `duracionMeses` allowed for a use and type, or null when the
 * pair does not exist (a campaign of free months). The single source for the
 * schema, the Functions and (mirrored) firestore.rules.
 */
export function rangosCupon(
  uso: UsoCupon,
  tipo: TipoCupon,
): { valor: Rango; duracion: Rango } | null {
  if (uso === 'campana') {
    return tipo === 'porcentaje'
      ? {
          valor: { min: 1, max: MAX_PORCENTAJE_CAMPANA },
          duracion: { min: 1, max: MAX_DURACION_CAMPANA },
        }
      : null;
  }
  return { valor: RANGO_VALOR_CUPON[tipo], duracion: RANGO_DURACION_CUPON };
}

/**
 * The use of a stored coupon. Coupons written before `uso` existed are read
 * conservatively: free months, or a percentage outside the campaign limits,
 * count as internal (they stop working on the site, as the plan requires).
 */
export function usoDe(c: {
  uso?: unknown;
  tipo?: unknown;
  valor?: unknown;
  duracionMeses?: unknown;
}): UsoCupon {
  if (c.uso === 'campana' || c.uso === 'interno') return c.uso;
  const campana =
    c.tipo === 'porcentaje' &&
    typeof c.valor === 'number' &&
    c.valor <= MAX_PORCENTAJE_CAMPANA &&
    (c.duracionMeses === undefined ||
      (typeof c.duracionMeses === 'number' && c.duracionMeses <= MAX_DURACION_CAMPANA));
  return campana ? 'campana' : 'interno';
}

export const MAX_DESCRIPCION_CUPON = 80;
export const MAX_USOS_CUPON = 100_000;

/** Firestore Timestamp as any SDK (admin or web) exposes it. */
export type MarcaTiempoCupon = { seconds: number; nanoseconds: number };

/** `cupones/{codigo}` as stored. */
export type Cupon<F extends MarcaTiempoCupon = MarcaTiempoCupon> = {
  codigo: string;
  /** Public text shown on the site, e.g. "15 % por 3 meses". */
  descripcion: string;
  /** Campaign (usable on the site) or internal (see USOS_CUPON). */
  uso: UsoCupon;
  tipo: TipoCupon;
  /** Percent off (1–30) or free months (1–12), per `tipo`. */
  valor: number;
  /** Months the benefit lasts (1–12); equals `valor` for `meses_gratis`. */
  duracionMeses: number;
  activo: boolean;
  vigenteDesde?: F;
  vigenteHasta?: F;
  usosMax?: number;
  usos: number;
  creadoEn: F;
  /** uid of the superadmin that created it. */
  creadoPor: string;
};

/** Snapshot of a valid coupon copied into the lead (`contactos/{id}.cupon`). */
export type CuponAplicado = Pick<
  Cupon,
  'codigo' | 'descripcion' | 'tipo' | 'valor' | 'duracionMeses'
>;

/**
 * Coupon of a stored lead (`contactos/{id}.cupon`): the snapshot the `contacto`
 * Function copied when the code was valid (active and in its dates; usos are
 * NOT counted then), plus what `convertirContacto` adds when the lead becomes
 * a customer: `aplicadoEn`/`aplicadoPor` when the use was counted, or
 * `noAplicado: true` when the superadmin converted it without the coupon
 * (exhausted, expired or deactivated by then). Neither while not converted.
 */
export type CuponDelContacto<F extends MarcaTiempoCupon = MarcaTiempoCupon> = CuponAplicado & {
  aplicadoEn?: F;
  /** uid of the superadmin that converted the lead. */
  aplicadoPor?: string;
  noAplicado?: true;
};

/**
 * Coupon fields of a stored lead (`contactos/{id}`), as the `contacto`
 * Function writes them in place of the typed code: `cupon` when it was
 * valid, `cuponInvalido` (the code) when a no-JS form sent an invalid one.
 * Never both.
 */
export type CuponDeContacto = { cupon?: CuponDelContacto; cuponInvalido?: string };

/** Lead as stored, before the server fields (estado, creadoEn). */
export type ContactoConCupon =
  | (Omit<ContactoDemo, 'cupon'> & CuponDeContacto)
  | (Omit<ContactoEspera, 'cupon'> & CuponDeContacto);

/** Fields `convertirContacto` adds to the lead (besides `estado: 'cliente'`). */
export type ConversionContacto<F extends MarcaTiempoCupon = MarcaTiempoCupon> = {
  convertidoEn: F;
  /** uid of the superadmin. */
  convertidoPor: string;
};

/**
 * Answer of `convertirContacto`. `cupon`: 'aplicado' (use counted now),
 * 'no_aplicado' (converted with sinCupon), 'ninguno' (the lead had no valid
 * coupon). `yaEraCliente`: an idempotent repeat that changed nothing (then
 * `cupon` describes the first conversion).
 */
export type RespuestaConvertirContacto = {
  yaEraCliente: boolean;
  cupon: 'aplicado' | 'no_aplicado' | 'ninguno';
};

/**
 * `details` of the failed-precondition error when the lead's coupon no longer
 * applies: the backoffice offers to retry with `sinCupon: true`.
 */
export type DetalleCuponNoAplica = {
  motivo: 'cupon_no_aplica';
  codigo: string;
  /**
   * Why: an EstadoCupon other than 'vigente', the coupon is gone or
   * malformed, or it is internal (only when checked as the site does).
   */
  estado: Exclude<EstadoCupon, 'vigente'> | 'inexistente' | 'mal_formado' | 'interno';
};

/** Public answer of `GET /api/cupon?codigo=X`. */
export type RespuestaCupon = { valido: true; descripcion: string } | { valido: false };

export type EstadoCupon = 'vigente' | 'inactivo' | 'pendiente' | 'vencido' | 'agotado';

export const ETIQUETAS_ESTADO_CUPON: Record<EstadoCupon, string> = {
  vigente: 'Vigente',
  inactivo: 'Desactivado',
  pendiente: 'Aún no vigente',
  vencido: 'Vencido',
  agotado: 'Agotado',
};

const millis = (m: MarcaTiempoCupon) => m.seconds * 1000 + Math.floor(m.nanoseconds / 1e6);

/**
 * State of a coupon at `ahora` (ms). Only 'vigente' can be applied: active,
 * inside [vigenteDesde, vigenteHasta) and with usos < usosMax.
 */
export function estadoCupon(
  c: {
    activo: boolean;
    usos: number;
    usosMax?: number | undefined;
    vigenteDesde?: MarcaTiempoCupon | undefined;
    vigenteHasta?: MarcaTiempoCupon | undefined;
  },
  ahora: number,
): EstadoCupon {
  if (c.activo !== true) return 'inactivo';
  if (c.vigenteDesde && millis(c.vigenteDesde) > ahora) return 'pendiente';
  if (c.vigenteHasta && millis(c.vigenteHasta) <= ahora) return 'vencido';
  if (typeof c.usosMax === 'number' && !(Number(c.usos) < c.usosMax)) return 'agotado';
  return 'vigente';
}

/** Share link of a coupon (the site reads ?cupon= and pre-fills the form). */
export const enlaceCupon = (codigo: string) =>
  `https://tallerza.cl/?cupon=${encodeURIComponent(codigo)}`;

export const MENSAJES_CUPON = {
  codigo: 'Usa de 3 a 20 letras, números o guiones.',
  descripcion: `Escribe una descripción de hasta ${MAX_DESCRIPCION_CUPON} caracteres.`,
  uso: 'Elige si es de campaña o interno.',
  tipo: 'Elige el tipo de beneficio.',
  valor: `Ingresa un valor válido: de 1 a ${MAX_PORCENTAJE_CUPON} % o de 1 a 12 meses.`,
  valorCampana: `En una campaña, el descuento va de 1 a ${MAX_PORCENTAJE_CAMPANA} %.`,
  duracionMeses: 'Ingresa una duración de 1 a 12 meses.',
  duracionCampana: `En una campaña, el descuento dura de 1 a ${MAX_DURACION_CAMPANA} meses.`,
  mesesGratisCampana: 'Los meses gratis son solo de uso interno, no para campañas.',
  usosMax: 'Ingresa un número entero mayor que cero.',
  vigencia: 'La fecha de término debe ser posterior a la de inicio.',
} as const;

const MC = MENSAJES_CUPON;

const entero = (min: number, max: number, error: string) =>
  z.number({ error }).check(z.int({ error }), z.gte(min, { error }), z.lte(max, { error }));

/**
 * Editable fields of a coupon, as the backoffice form produces them (dates
 * as Date; the page converts them to Timestamp). `codigo` and `usos` are set
 * on creation only (usos = 0); the rules reject changes to them later.
 * Limits per use and type come from `rangosCupon`.
 */
export const esquemaCupon = z
  .object({
    codigo: z
      .string({ error: MC.codigo })
      .check(z.overwrite(normalizarCodigoCupon), z.regex(REGEX_CODIGO_CUPON, { error: MC.codigo })),
    descripcion: z
      .string({ error: MC.descripcion })
      .check(
        z.trim(),
        z.minLength(3, { error: MC.descripcion }),
        z.maxLength(MAX_DESCRIPCION_CUPON, { error: MC.descripcion }),
      ),
    uso: z.enum(USOS_CUPON, { error: MC.uso }),
    tipo: z.enum(TIPOS_CUPON, { error: MC.tipo }),
    valor: entero(1, MAX_PORCENTAJE_CUPON, MC.valor),
    duracionMeses: entero(RANGO_DURACION_CUPON.min, RANGO_DURACION_CUPON.max, MC.duracionMeses),
    activo: z.boolean(),
    vigenteDesde: z.optional(z.date()),
    vigenteHasta: z.optional(z.date()),
    usosMax: z.optional(entero(1, MAX_USOS_CUPON, MC.usosMax)),
  })
  .check(
    // Free months: the duration is the number of months itself.
    z.overwrite((c) => (c.tipo === 'meses_gratis' ? { ...c, duracionMeses: c.valor } : c)),
    z.refine((c) => rangosCupon(c.uso, c.tipo) !== null, {
      error: MC.mesesGratisCampana,
      path: ['tipo'],
    }),
    z.refine(
      (c) => c.valor >= RANGO_VALOR_CUPON[c.tipo].min && c.valor <= RANGO_VALOR_CUPON[c.tipo].max,
      { error: MC.valor, path: ['valor'] },
    ),
    // Campaign limits (rangosCupon), each on its own field.
    z.refine(
      (c) => c.uso !== 'campana' || c.tipo !== 'porcentaje' || c.valor <= MAX_PORCENTAJE_CAMPANA,
      { error: MC.valorCampana, path: ['valor'] },
    ),
    z.refine(
      (c) =>
        c.uso !== 'campana' || c.tipo !== 'porcentaje' || c.duracionMeses <= MAX_DURACION_CAMPANA,
      { error: MC.duracionCampana, path: ['duracionMeses'] },
    ),
    z.refine((c) => !c.vigenteDesde || !c.vigenteHasta || c.vigenteHasta > c.vigenteDesde, {
      error: MC.vigencia,
      path: ['vigenteHasta'],
    }),
  );

export type FormularioCupon = z.infer<typeof esquemaCupon>;

/**
 * Non-blocking note for the backoffice form about what an internal coupon
 * means. Null for a campaign coupon (its limits are rules, not notes).
 */
export function advertenciaCupon(c: { uso: UsoCupon }): string | null {
  if (c.uso === 'interno') {
    return 'Un cupón interno no se puede usar en tallerza.cl: lo aplicas tú en la ficha comercial del taller.';
  }
  return null;
}
